import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ChatHome } from "./chat/ChatHome";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { LoadingScreen } from "./components/LoadingScreen";
import { useAuth } from "./hooks/useAuth";
import { CreateProject } from "./pages/CreateProject";
import { CreateWorkspace } from "./pages/CreateWorkspace";
import { Dashboard } from "./pages/Dashboard";
import { ProjectPage } from "./pages/ProjectPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Telegram Mini Apps are typically short-lived. Avoid aggressive refetching.
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

function AuthGate({ children }: { children: ReactNode }): React.ReactElement {
  const { isReady, error, user } = useAuth();

  if (!isReady) {
    return <LoadingScreen message="Authenticating…" />;
  }

  if (error?.kind === "no-init-data") {
    return (
      <Centered
        title="Open from Telegram"
        body="This app must be opened from inside the AI Memory bot in Telegram."
      />
    );
  }

  if (error || !user) {
    return (
      <Centered
        title="Sign-in failed"
        body={error ? error.message : "Unknown error"}
        hint="Close the Mini App and reopen it from the bot."
      />
    );
  }

  return <>{children}</>;
}

function Centered({
  title,
  body,
  hint,
}: {
  title: string;
  body: string;
  hint?: string;
}): React.ReactElement {
  return (
    <div
      className="min-h-dvh flex flex-col items-center justify-center gap-2 px-6 text-center"
      style={{ background: "var(--color-bg)", color: "var(--color-text)" }}
    >
      <h2 className="text-[16px] font-semibold">{title}</h2>
      <p className="text-[13px]" style={{ color: "var(--color-hint)" }}>
        {body}
      </p>
      {hint ? (
        <p className="text-[12px]" style={{ color: "var(--color-hint)" }}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function App(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <BrowserRouter>
          <AuthGate>
            <Routes>
              {/* Chat-first redesign — ChatHome is the new primary surface;
                  the old Dashboard is still reachable via the header pill. */}
              <Route path="/" element={<ChatHome />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/projects/new" element={<CreateProject />} />
              <Route path="/projects/:id" element={<ProjectPage />} />
              <Route path="/workspaces/new" element={<CreateWorkspace />} />
              <Route
                path="*"
                element={
                  <Centered
                    title="Page not found"
                    body="The route you opened doesn't exist."
                  />
                }
              />
            </Routes>
          </AuthGate>
        </BrowserRouter>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}
